import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Spinner,
  TextBlock,
  Title,
  useAdaptive,
  useModal,
} from '@skillarea/lib-frontend';
import { DailyRewardStateDto } from '@skillarea/lib-shared';
import { Trans, useTranslation } from 'react-i18next';

import cx from 'classnames';

import { ChestSlot } from './ChestSlot';
import styles from './Chests.module.scss';
import {
  applyChestPreviewOpen,
  createChestPreviewState,
  getChestPreviewReward,
  isChestPreviewEnabled,
} from './chestPreview';
import { normalizeSingle } from './chestUtils';
import { useChestOpening } from './useChestOpening';

import { ChestAlreadyOpenedModal } from '~/components/ChestAlreadyOpenedModal/ChestAlreadyOpenedModal';
import { ChestNotPlayedModal } from '~/components/ChestNotPlayedModal/ChestNotPlayedModal';
import { DailyRewardService } from '~/services/dailyRewardService';

export const Chests: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { isMobile } = useAdaptive();
  const { openModal } = useModal();
  const chestsSectionRef = useRef<HTMLElement | null>(null);
  const dailyRewardService = useMemo(() => new DailyRewardService(), []);
  const isPreviewMode = isChestPreviewEnabled();
  const previewReward = getChestPreviewReward();
  const [previewState, setPreviewState] = useState(createChestPreviewState);

  useEffect(() => {
    if (typeof window === 'undefined' || window.location.hash !== '#rewards') {
      return;
    }

    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)')
      .matches
      ? 'auto'
      : 'smooth';

    window.requestAnimationFrame(() => {
      chestsSectionRef.current?.scrollIntoView({
        behavior,
        block: 'start',
      });
    });
  }, []);

  const dailyRewardQuery = useQuery({
    queryKey: ['daily-rewards', 'state'],
    enabled: !isPreviewMode,
    queryFn: async () =>
      normalizeSingle<DailyRewardStateDto>(await dailyRewardService.getState()),
  });

  const {
    isMutationPending,
    isSlotTransitionActive,
    openingState,
    resetOpening,
    startOpen,
  } = useChestOpening({
    isPreviewMode,
    onPreviewOpenSuccess: (result) => {
      setPreviewState((current) => applyChestPreviewOpen(current, result));
    },
    previewReward,
    rewardState: isPreviewMode ? previewState : dailyRewardQuery.data,
  });

  const state = isPreviewMode ? previewState : dailyRewardQuery.data;
  const slots = state?.slots ?? [];
  const currentDayIndex = state?.currentDayIndex;

  const visibleSlots = useMemo(() => {
    if (!isMobile || slots.length < 3 || currentDayIndex === undefined) {
      return slots;
    }
    const currentIndex = slots.findIndex((s) => s.dayIndex === currentDayIndex);
    if (currentIndex === -1) return slots;

    let startIndex = currentIndex - 1;
    if (startIndex < 0) startIndex = 0;
    if (startIndex + 3 > slots.length) startIndex = slots.length - 3;

    return slots.slice(startIndex, startIndex + 3);
  }, [isMobile, slots, currentDayIndex]);

  const showAlreadyOpenedModal = () => {
    openModal(<ChestAlreadyOpenedModal />, {}, true);
  };

  const showNotPlayedModal = () => {
    openModal(<ChestNotPlayedModal />, {}, true);
  };

  const resetPreview = () => {
    setPreviewState(createChestPreviewState());
    resetOpening();
  };

  return (
    <section
      className={styles.chestsSection}
      id="rewards"
      ref={chestsSectionRef}
    >
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Title
            className={cx(styles.title, styles.playEveryTitle)}
            color="contrast"
            size="xl"
            variant="benzin"
          >
            <Trans
              components={{
                accent: (
                  <Title
                    className={styles.titleInsertion}
                    color="accent"
                    isInsertion
                    size="l"
                    variant="cormorant"
                  />
                ),
                br: <br />,
              }}
              i18nKey="chests.title"
              ns="dashboard"
            />
          </Title>
          <TextBlock className={styles.wording} color="contrast" size="m">
            {t('chests.wording')}
          </TextBlock>
        </div>
      </div>

      {isPreviewMode ? (
        <div className={styles.previewBanner}>
          <TextBlock color="contrast" size="s">
            Chest preview: {previewReward}. API is disabled.
          </TextBlock>
          <Button
            color="accent"
            onClick={resetPreview}
            size="s"
            variant="filled"
          >
            Reset preview
          </Button>
        </div>
      ) : null}

      {!isPreviewMode && dailyRewardQuery.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : !isPreviewMode && dailyRewardQuery.isError ? (
        <div className={styles.error}>
          <TextBlock className={styles.errorText} color="contrast" size="m">
            {t('chests.loadError')}
          </TextBlock>
          <Button
            color="accent"
            onClick={() => dailyRewardQuery.refetch()}
            size="s"
            variant="filled"
          >
            {t('chests.retry')}
          </Button>
        </div>
      ) : (
        <div className={styles.chestsRowWrap}>
          <div className={styles.chestsRow}>
            {visibleSlots.map((slot) => (
              <ChestSlot
                currentDayIndex={currentDayIndex}
                isMutationPending={isMutationPending}
                isSlotTransitionActive={isSlotTransitionActive}
                key={slot.dayIndex}
                onOpenChest={startOpen}
                onShowAlreadyOpenedModal={showAlreadyOpenedModal}
                onShowNotPlayedModal={showNotPlayedModal}
                openingState={openingState}
                rewardState={state}
                slot={slot}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
